import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { cleanAnswer } from '@source/backup/sanitize-llm-response.js';
import { tagLlmResponse } from '../api/tag.js';

export enum AnsweringChannelType {
  INTERCOM_CONVERSATION = 'INTERCOM_CONVERSATION',
  MAIL = 'MAIL',
  INTEGRATION_TEST_EMAIL = 'INTEGRATION_TEST_EMAIL',
  INTEGRATION_TEST_CHAT = 'INTEGRATION_TEST_CHAT',
  DEMO = 'DEMO',
}

registerEnum({ enum: AnsweringChannelType, name: 'AnsweringChannelType' });

@Entity()
export class AnsweringEntity extends MongoEntity {
  static override _class = 'AnsweringEntity';
  static override _collectionName = 'Answering';

  @Field(() => String) foreignKey: Maybe<string>; // -> OdooEntity ticketID
  @Field(() => String) conversationPartId: Maybe<string>;
  @Field(() => String) query: Maybe<string>;
  @Field(() => String) answer: Maybe<string>;
  @Field(() => String) expectedAnswer: Maybe<string>; // answer from odoo
  @Field(() => String) from: Maybe<string>;
  @Field(() => AnsweringChannelType) answeringChannelType: Maybe<AnsweringChannelType>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => String) originalCreatedAt: Maybe<String>;
  @Field(() => [String]) odooTags: Maybe<string[]>;
  @Field(() => Float) rating: Maybe<number>;
  @Field(() => String) ratingText: Maybe<string>;
  @Field(() => String) integrationTestKey: Maybe<string>;
  @Field(() => String) knowledgeDeltas: Maybe<string>; // Unique identifier for the ticket
  @Field(() => [String]) comments: Maybe<string[]>; // Array to store user comments
  @Field(() => Boolean) isCheckedBySupport: Maybe<boolean>;
  @Field(() => Boolean) checkedByHuman: Maybe<boolean>;

  static override async beforeCreate(entity: AnsweringEntity) {
    entity.createdAt = new Date();
    return super.beforeCreate(entity);
  }
}

// ensure indexes on startup
void (async () => {
  if (!A.isServer) return;
  const collection = await getRawCollection<AnsweringEntity>(AnsweringEntity._collectionName);
  await collection.createIndex({ foreignKey: 1 });
  await collection.createIndex({ answeringChannelType: 1, createdAt: 1 });
})();

export async function setOdooTagsByItemId(newOdooTags: string[], ticketId: string) {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  await collection.updateOne({ _id: ticketId }, { $set: { odooTags: newOdooTags } });
}

export async function setAnswerByItemId(answer: string, ticketId: string) {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  await collection.updateOne({ _id: ticketId }, { $set: { answer: answer } });
}

export async function getAnsweringByForeignKeyAndConversationPartId(
  foreignKey: string,
  conversationPartId: string,
): Promise<AnsweringEntity | null> {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  const item = await collection.findOne({ foreignKey: foreignKey, conversationPartId: conversationPartId });
  if (!item) {
    logger.warn(`No AnsweringEntity found for foreignKey ${foreignKey} and conversationPartId ${conversationPartId}`);
    return null;
  }
  return new AnsweringEntity(item);
}

/**
 * Finds the AnsweringEntity with the highest conversationPartId for a given foreignKey
 * and updates its expectedAnswer field (appending if already set).
 */
export async function findAndUpdateAnswerByFrKeyMissingExpectedAnswer(
  foreignKey: string,
  expectedAnswer: string,
): Promise<AnsweringEntity | null> {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  const itemsWithMissingExpectedAnswer = await collection
    .find({
      foreignKey: foreignKey,
      conversationPartId: { $exists: true, $ne: null },
    })
    .toArray();
  if (itemsWithMissingExpectedAnswer.length === 0) {
    return null;
  }
  let itemWithHighestConversationPartId = itemsWithMissingExpectedAnswer.reduce((prev, current) => {
    const prevId = Number(prev.conversationPartId) || 0;
    const currentId = Number(current.conversationPartId) || 0;
    return prevId > currentId ? prev : current;
  });
  if (!itemWithHighestConversationPartId) {
    logger.warn(`No items found with foreignKey ${foreignKey} and missing expectedAnswer`);
    return null;
  }
  // if itemWithHighestConversationPartId.expectedAnswer is not set yet, update it with expectedAnswer, otherwise concatenate
  if (!itemWithHighestConversationPartId.expectedAnswer) {
    itemWithHighestConversationPartId.expectedAnswer = expectedAnswer;
  } else {
    itemWithHighestConversationPartId.expectedAnswer += `; ${expectedAnswer}`;
  }
  await collection.updateOne(
    { _id: itemWithHighestConversationPartId._id },
    { $set: { expectedAnswer: itemWithHighestConversationPartId.expectedAnswer } },
  );
  logger.info(
    `Updated expected answer for item with foreignKey ${foreignKey} to ${itemWithHighestConversationPartId.expectedAnswer}`,
  );
  const lastItemResponse = new AnsweringEntity(itemWithHighestConversationPartId);
  return lastItemResponse;
}

export async function setMissingTags() {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  let entities = await collection
    .find({
      $or: [{ odooTags: { $exists: false } }, { odooTags: null }, { odooTags: [] }, { odooTags: { $size: 0 } }],
    })
    .toArray();

  console.log('Entities without tags:', entities.length);

  if (entities.length > 100) {
    entities = entities.slice(0, 100);
  }

  for (const entity of entities) {
    console.log('Processing entity:', entity._id, 'with query:', entity.query);
    let llmResponseOnlyMessage = await tagLlmResponse(entity.query);
    llmResponseOnlyMessage = cleanAnswer(llmResponseOnlyMessage);
    logger.info({ llmResponseOnlyMessage }, 'LLM Response:');
    try {
      const responseJson = JSON.parse(llmResponseOnlyMessage);
      let tags = responseJson.tags || [];
      await collection.updateOne({ _id: entity._id }, { $set: { odooTags: tags } });
    } catch (error) {
      logger.error({ err: error }, 'Failed to parse LLM response');
      await collection.updateOne({ _id: entity._id }, { $set: { odooTags: [] } });
    }
    logger.info(`Setting missing tags for entity with ID: ${entity._id}`);
  }

  return entities.length;
}

export async function rateableAnsweringItems() {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  const items = await collection
    .find({
      expectedAnswer: { $exists: true, $ne: null },
      query: { $exists: true, $ne: null },
      answer: { $exists: true, $ne: null },
      $or: [
        { rating: { $exists: false } },
        { rating: null },
        { rating: { $eq: 0 } },
        { ratingText: { $exists: false } },
        { ratingText: null },
      ],
      createdAt: { $lte: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2) }, // consider items older than 3 days
    })
    .toArray();
  return items.map(item => new AnsweringEntity(item));
}

export async function getOdooItemsMissingAnswersCursor(fromDate?: Date) {
  const collection = await getCollection<AnsweringEntity>(AnsweringEntity._class);
  const items = collection.find({
    $and: [
      {
        $or: [{ expectedAnswer: { $in: [null, undefined, '', '-'] } }, { expectedAnswer: { $exists: false } }],
      },
      { foreignKey: { $exists: true, $nin: [null, undefined, ''] } },
      { conversationPartId: { $exists: true, $nin: [null, undefined, ''] } },
      ...(fromDate ? [{ createdAt: { $gte: fromDate } }] : []),
    ],
    answeringChannelType: AnsweringChannelType.MAIL,
  });
  return items;
}
